/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * LookupField — a form field whose choices come from a real table.
 *
 * Three display modes, chosen per field by whoever built the form:
 *   dropdown — a plain select, for a short list
 *   search   — type to filter, for hundreds of rows
 *   popup    — a button opening a search dialog, for thousands
 *
 * All three read from ONE endpoint:
 *   GET /arcee/public/form/<form_id>/lookup/<field_id>/
 *
 * Note what that URL does not contain: a table name. The request names a form
 * and a field; the server reads which table, which label column and which rows
 * from the field's stored configuration. So this component cannot be pointed at
 * data the form was not configured to offer, however it is called.
 *
 * It lives in its own file rather than inside FormBuilderSection because that
 * file is already 65KB, and a picker with its own fetching, debouncing and
 * modal is the kind of thing that should be readable on its own.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import APIHelper from '../../../api/APIHelper';
import envConfig from '../../../config/envConfig';

const apiHelper = new APIHelper({
  apiUrl:       envConfig.backendApiUrl,
  tokenUrl:     envConfig.backendTokenUrl,
  clientId:     envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

const SEARCH_DEBOUNCE_MS = 250;

/** One page of options for a field. Signed-in only — the endpoint requires it. */
async function fetchOptions(formId, fieldId, { search, page, dependsValue }) {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  if (page && page > 1) params.set('page', String(page));
  if (dependsValue !== undefined && dependsValue !== null && dependsValue !== '') {
    params.set('depends_value', String(dependsValue));
  }
  const query = params.toString();
  const resp = await apiHelper.apiCall(
    `/arcee/public/form/${formId}/lookup/${fieldId}/${query ? `?${query}` : ''}`
  );
  if (!resp.ok) {
    const problem = new Error('lookup_failed');
    problem.status = resp.status;
    throw problem;
  }
  return resp.json();
}

/**
 * Loads options for one field, re-loading when the search text or the field it
 * depends on changes. Returns everything the three modes need.
 */
function useLookupOptions(formId, field, dependsValue, { enabled = true } = {}) {
  const [options, setOptions]   = useState([]);
  const [loading, setLoading]   = useState(false);
  // Why it failed, not just that it did: a lookup on a form that requires a
  // sign-in fails for a reason the person can act on, and 'try again' is the
  // one thing that will never help them.
  const [failed, setFailed]     = useState(null);
  const [hasMore, setHasMore]   = useState(false);
  const [page, setPage]         = useState(1);
  const [search, setSearch]     = useState('');
  const debounced = useRef(null);
  const requestId = useRef(0);

  const load = useCallback(async (nextSearch, nextPage, append) => {
    if (!enabled || !formId) return;
    const id = ++requestId.current;
    setLoading(true);
    setFailed(null);
    try {
      const body = await fetchOptions(formId, field.id, {
        search: nextSearch, page: nextPage, dependsValue,
      });
      // A slower earlier request must not overwrite a newer one's results.
      if (id !== requestId.current) return;
      setOptions(prev => (append ? [...prev, ...(body.results || [])] : (body.results || [])));
      setHasMore(!!body.has_more);
      setPage(nextPage);
    } catch (err) {
      if (id !== requestId.current) return;
      setFailed({ status: err && err.status });
      if (!append) setOptions([]);
      setHasMore(false);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [enabled, formId, field.id, dependsValue]);

  // Re-load whenever the upstream field changes — "cities in the country they
  // picked" has to forget the previous country's cities.
  useEffect(() => {
    load(search, 1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependsValue, enabled]);

  const onSearchChange = useCallback((text) => {
    setSearch(text);
    if (debounced.current) clearTimeout(debounced.current);
    debounced.current = setTimeout(() => load(text, 1, false), SEARCH_DEBOUNCE_MS);
  }, [load]);

  useEffect(() => () => { if (debounced.current) clearTimeout(debounced.current); }, []);

  const loadMore = useCallback(() => {
    if (!loading && hasMore) load(search, page + 1, true);
  }, [loading, hasMore, load, search, page]);

  return { options, loading, failed, hasMore, search, onSearchChange, loadMore, reload: load };
}

function listItemStyle(t, active) {
  return {
    padding: '8px 12px',
    fontSize: t.inputFontSize,
    color: t.inputTextColor,
    cursor: 'pointer',
    background: active ? `${t.inputFocusRingColor}18` : 'transparent',
    borderBottom: `1px solid ${t.inputBorderColor}`,
    textAlign: 'left',
    width: '100%',
    border: 'none',
    display: 'block',
  };
}

function panelStyle(t) {
  return {
    background: t.inputBgColor,
    border: `1px solid ${t.inputBorderColor}`,
    borderRadius: t.inputBorderRadius,
    maxHeight: 240,
    overflowY: 'auto',
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
  };
}

function Message({ t, children }) {
  return (
    <div style={{ padding: '10px 12px', fontSize: t.helpTextFontSize + 1, color: t.helpTextColor }}>
      {children}
    </div>
  );
}

function OptionList({ t, options, loading, failed, hasMore, onPick, onLoadMore, selected }) {
  if (failed) {
    return (
      <Message t={t}>
        {failed.status === 401
          ? 'Sign in to choose from this list.'
          : 'Could not load choices. Please try again.'}
      </Message>
    );
  }
  if (!loading && !options.length) {
    return <Message t={t}>No matches.</Message>;
  }
  return (
    <>
      {options.map(opt => (
        <button
          type="button"
          key={String(opt.value)}
          onClick={() => onPick(opt)}
          style={listItemStyle(t, String(opt.value) === String(selected))}
        >
          {opt.label}
        </button>
      ))}
      {loading && <Message t={t}>Loading…</Message>}
      {hasMore && !loading && (
        <button type="button" onClick={onLoadMore}
          style={{ ...listItemStyle(t, false), color: t.inputFocusRingColor, fontWeight: 600 }}>
          Show more
        </button>
      )}
    </>
  );
}

export default function LookupField({ field, value, onChange, tokens, error, formId, values, locked }) {
  const t = tokens;
  const config = field.lookup || {};
  const mode = config.mode || 'dropdown';

  // A dependent lookup waits for the field it depends on. Until that is chosen
  // the server returns nothing, so there is no point asking.
  const dependsField = (config.depends_on || {}).field;
  const dependsValue = dependsField ? (values || {})[dependsField] : undefined;
  const waitingOnParent = !!dependsField && (dependsValue === undefined || dependsValue === '' || dependsValue === null);
  // Locked and waiting look the same to a person — the control does not respond
  // — but they are different facts, so they stay separate and only the reason
  // shown differs.
  const disabled = locked || waitingOnParent;

  const [open, setOpen] = useState(false);
  const [pickedLabel, setPickedLabel] = useState('');
  const wrapper = useRef(null);

  const {
    options, loading, failed, hasMore, search, onSearchChange, loadMore,
  } = useLookupOptions(formId, field, dependsValue, { enabled: !waitingOnParent });

  // Clear a chosen value when the field it depends on changes underneath it —
  // a city from the previous country would otherwise stay selected and be
  // submitted.
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    if (!dependsField) return;
    onChange(field.id, '');
    setPickedLabel('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependsValue]);

  // The label for whatever is currently selected: what the person picked, or
  // the matching option once the list has loaded.
  const currentLabel = pickedLabel
    || (options.find(o => String(o.value) === String(value)) || {}).label
    || (value ? String(value) : '');

  const pick = (opt) => {
    onChange(field.id, opt.value);
    setPickedLabel(opt.label);
    setOpen(false);
  };

  useEffect(() => {
    if (!open || mode === 'popup') return undefined;
    const onDocClick = (e) => {
      if (wrapper.current && !wrapper.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open, mode]);

  const controlStyle = {
    background: t.inputBgColor,
    border: `${t.inputBorderWidth}px ${t.inputBorderStyle} ${error ? t.inputBorderColorError : t.inputBorderColor}`,
    borderRadius: t.inputBorderRadius,
    padding: `${t.inputPaddingY}px ${t.inputPaddingX}px`,
    fontSize: t.inputFontSize,
    fontFamily: t.inputFontFamily || 'inherit',
    color: currentLabel ? t.inputTextColor : t.inputPlaceholderColor,
    width: '100%',
    height: t.inputHeight,
    boxSizing: 'border-box',
    textAlign: 'left',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: locked ? 0.65 : 1,
  };

  const placeholder = waitingOnParent
    ? (config.waiting_text || 'Choose the field above first')
    : (field.placeholder || 'Select…');

  // ── Plain dropdown ────────────────────────────────────────────────────
  if (mode === 'dropdown') {
    return (
      <select
        id={`field_${field.id}`}
        value={value || ''}
        required={field.required}
        disabled={disabled}
        onChange={e => {
          const opt = options.find(o => String(o.value) === e.target.value);
          if (opt) pick(opt); else onChange(field.id, e.target.value);
        }}
        style={controlStyle}
      >
        <option value="">{loading ? 'Loading…' : placeholder}</option>
        {options.map(opt => (
          <option key={String(opt.value)} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    );
  }

  // ── Search popup ──────────────────────────────────────────────────────
  if (mode === 'popup') {
    return (
      <>
        <button type="button" style={controlStyle} disabled={disabled}
          onClick={() => setOpen(true)}>
          {currentLabel || placeholder}
        </button>
        {open && (
          <div
            style={{
              position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.45)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
            }}
            onClick={() => setOpen(false)}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: t.formBgColor || '#fff', borderRadius: 12, width: '100%',
                maxWidth: 460, padding: 18, boxShadow: '0 12px 40px rgba(0,0,0,0.24)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <strong style={{ fontSize: t.labelFontSize + 2, color: t.inputTextColor }}>
                  {field.label}
                </strong>
                <button type="button" onClick={() => setOpen(false)}
                  style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 18, color: t.helpTextColor }}>
                  ×
                </button>
              </div>
              <input
                autoFocus
                value={search}
                onChange={e => onSearchChange(e.target.value)}
                placeholder="Search…"
                style={{ ...controlStyle, cursor: 'text', color: t.inputTextColor, marginBottom: 10 }}
              />
              <div style={panelStyle(t)}>
                <OptionList t={t} options={options} loading={loading} failed={failed}
                  hasMore={hasMore} onPick={pick} onLoadMore={loadMore} selected={value} />
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // ── Type-to-search dropdown ───────────────────────────────────────────
  return (
    <div ref={wrapper} style={{ position: 'relative' }}>
      <input
        id={`field_${field.id}`}
        value={open ? search : currentLabel}
        disabled={disabled}
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onChange={e => { setOpen(true); onSearchChange(e.target.value); }}
        style={{ ...controlStyle, cursor: disabled ? 'not-allowed' : 'text', color: t.inputTextColor }}
      />
      {open && !disabled && (
        <div style={{ ...panelStyle(t), position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 40, marginTop: 4 }}>
          <OptionList t={t} options={options} loading={loading} failed={failed}
            hasMore={hasMore} onPick={pick} onLoadMore={loadMore} selected={value} />
          {config.allow_browse_all !== false && (
            <Message t={t}>Keep typing to narrow the list.</Message>
          )}
        </div>
      )}
    </div>
  );
}
